import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errText, photoUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Activity as ActivityIcon, Download, RotateCcw, Loader2, Users, Eye, Package, Clock, Flag, MessageCircle, ShieldCheck, BadgeCheck, Star, MousePointerClick, UserPlus, TrendingUp, AlertTriangle, Palette } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ACTION_META, timeAgo } from "./adminShared";

function ScoreCard() {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get(`/admin/activity/scorecard?days=${days}`).then((r) => setRows(r.data.scorecard)).catch((e) => toast.error(errText(e)));
  }, [days]);
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mb-5" data-testid="mod-scorecard">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><ActivityIcon size={18} className="text-[#F5551E]" /> Moderator scorecard</h3>
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          {[7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`scorecard-${d}d`}
              className={`px-3 h-8 rounded-md text-sm font-semibold transition-colors ${days === d ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {!rows ? (
        <div className="grid sm:grid-cols-2 gap-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No staff actions in the last {days} days.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((r) => (
            <div key={r.actor_id} className="border border-slate-100 rounded-xl p-4" data-testid={`scorecard-row-${r.actor_id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-8 w-8 rounded-full bg-[#12234A] text-white text-xs font-bold flex items-center justify-center">{r.name?.[0]}</span>
                <div><p className="font-semibold text-[#12234A] text-sm leading-tight">{r.name}</p><p className="text-xs text-slate-400 capitalize">{r.role}</p></div>
                <span className="ml-auto text-xs text-slate-400">{r.total} total</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 rounded-lg py-2"><p className="font-display text-lg font-extrabold text-emerald-600">{r.approvals}</p><p className="text-[11px] text-slate-500">Approvals</p></div>
                <div className="bg-red-50 rounded-lg py-2"><p className="font-display text-lg font-extrabold text-red-600">{r.removals}</p><p className="text-[11px] text-slate-500">Removals</p></div>
                <div className="bg-orange-50 rounded-lg py-2"><p className="font-display text-lg font-extrabold text-[#F5551E]">{r.replies}</p><p className="text-[11px] text-slate-500">Replies</p></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ActivityLog() {
  const [data, setData] = useState(null);
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (actor !== "all") params.set("actor", actor);
    if (action !== "all") params.set("action", action);
    api.get(`/admin/activity?${params.toString()}`).then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  }, [actor, action]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (actor !== "all") params.set("actor", actor);
      if (action !== "all") params.set("action", action);
      const res = await api.get(`/admin/activity/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "critterposts-activity-log.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch (e) { toast.error(errText(e)); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <ScoreCard />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Staff</span>
          <Select value={actor} onValueChange={setActor}>
            <SelectTrigger className="w-44 rounded-xl bg-white" data-testid="activity-actor-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All staff</SelectItem>
              {(data?.actors || []).map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.role})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Action</span>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-48 rounded-xl bg-white" data-testid="activity-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="all">All actions</SelectItem>
              {Object.entries(ACTION_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="activity-export" className="rounded-xl gap-1.5">{exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV</Button>
          <Button variant="outline" onClick={load} data-testid="activity-refresh" className="rounded-xl gap-1.5"><RotateCcw size={15} /> Refresh</Button>
        </div>
      </div>

      {!data ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : data.entries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-400" data-testid="activity-empty">
          <ActivityIcon size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium text-slate-500">No activity yet</p>
          <p className="text-sm">Moderator and admin actions will appear here as they happen.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/80 divide-y divide-slate-100" data-testid="activity-list">
          {data.entries.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, icon: ActivityIcon, color: "#64748B" };
            const Icon = meta.icon;
            return (
              <div key={e.id} className="flex items-start gap-3 p-4" data-testid={`activity-item-${e.id}`}>
                <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${meta.color}18` }}><Icon size={17} style={{ color: meta.color }} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#12234A]">
                    <span className="font-semibold">{e.actor_name}</span>
                    <span className="text-slate-500"> {meta.label.toLowerCase()} </span>
                    {e.target_label && <span className="font-medium">{e.target_label}</span>}
                  </p>
                  {e.action === "message_sent" && e.meta?.preview && (
                    <p className="text-xs text-slate-500 mt-0.5 truncate italic">“{e.meta.preview}”{e.meta.listing_title ? ` · re: ${e.meta.listing_title}` : ""}</p>
                  )}
                  {e.action === "listing_rejected" && e.meta?.reason && (
                    <p className="text-xs text-slate-500 mt-0.5 truncate">Reason: {e.meta.reason}</p>
                  )}
                  {e.action === "user_role_changed" && e.meta?.role && (
                    <p className="text-xs text-slate-500 mt-0.5">New role: <span className="capitalize">{e.meta.role}</span></p>
                  )}
                  {e.action === "user_updated" && e.meta?.fields && (
                    <p className="text-xs text-slate-500 mt-0.5">Updated: {e.meta.fields.join(", ")}</p>
                  )}
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">{timeAgo(e.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LiveNow() {
  const [p, setP] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => api.get("/admin/presence").then((r) => { if (active) setP(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, []);
  return (
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="live-online-users">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-50"><Users size={20} className="text-emerald-600" /></span>
        </div>
        <p className="font-display text-3xl font-extrabold text-[#12234A] mt-3" data-testid="live-online-users-count">{p ? p.online_users : "—"}</p>
        <p className="text-sm text-slate-500">Members online now</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="live-online-visitors">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sky-500" />
          </span>
          <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-sky-50"><Eye size={20} className="text-sky-600" /></span>
        </div>
        <p className="font-display text-3xl font-extrabold text-[#12234A] mt-3" data-testid="live-online-visitors-count">{p ? p.online_visitors : "—"}</p>
        <p className="text-sm text-slate-500">Guests browsing (no profile)</p>
      </div>
    </div>
  );
}

function MiniTrend({ title, data, total, color, icon: Icon, gradId, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}><Icon size={17} style={{ color }} /></span>
        <div>
          <p className="text-sm text-slate-500 leading-tight">{title}</p>
          <p className="font-display text-xl font-extrabold text-[#12234A] leading-tight" data-testid={`${testid}-total`}>{total}<span className="text-xs font-medium text-slate-400 ml-1">last 30 days</span></p>
        </div>
      </div>
      <div className="mt-3 -mx-1">
        <ResponsiveContainer width="100%" height={110}>
          <AreaChart data={data} margin={{ top: 5, right: 6, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 10, fill: "#94a3b8" }} interval={6} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} width={28} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
              labelFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              formatter={(val) => [val, title]}
            />
            <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2} fill={`url(#${gradId})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function TrendCharts() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/stats/trends").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="grid sm:grid-cols-2 gap-4 mt-4">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="overview-trends">
      <MiniTrend title="New signups" data={d.signups} total={d.totals.signups} color="#12234A" icon={UserPlus} gradId="grad-signups" testid="trend-signups" />
      <MiniTrend title="New listings" data={d.listings} total={d.totals.listings} color="#F5551E" icon={TrendingUp} gradId="grad-listings" testid="trend-listings" />
    </div>
  );
}

export function Overview({ onNavigate }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(errText(e))); }, []);
  const cards = [
    { icon: Users, label: "Total users", key: "users", color: "#12234A", tab: "users" },
    { icon: Package, label: "Active listings", key: "active_listings", color: "#F5551E", tab: "listings" },
    { icon: Clock, label: "Pending approval", key: "pending_listings", color: "#D97706", tab: "pending" },
    { icon: Flag, label: "Open reports", key: "open_reports", color: "#DC2626", tab: "reports" },
    { icon: Package, label: "Removed listings", key: "removed_listings", color: "#64748B", tab: "listings" },
    { icon: MessageCircle, label: "Conversations", key: "conversations", color: "#0EA5E9", tab: "conversations" },
    { icon: ShieldCheck, label: "Moderators", key: "moderators", color: "#10B981", tab: "users" },
    { icon: BadgeCheck, label: "Pending verifications", key: "pending_verifications", color: "#7C3AED", tab: "verification" },
  ];
  if (!s) return <div className="grid sm:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  return (
    <>
      <LiveNow />
      {(s.overdue_design > 0 || s.high_risk_users > 0) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center gap-3" data-testid="overview-alert">
          <span className="flex items-center gap-2 text-amber-700 font-semibold text-sm"><AlertTriangle size={18} /> Needs attention</span>
          {s.overdue_design > 0 && (
            <button onClick={() => onNavigate("contact")} data-testid="alert-overdue-design" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors">
              <Palette size={13} /> {s.overdue_design} design request{s.overdue_design > 1 ? "s" : ""} overdue 48h+
            </button>
          )}
          {s.high_risk_users > 0 && (
            <button onClick={() => onNavigate("users")} data-testid="alert-high-risk" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-white border border-red-300 text-red-700 hover:bg-red-50 transition-colors">
              <ShieldCheck size={13} /> {s.high_risk_users} high-risk user{s.high_risk_users > 1 ? "s" : ""}
            </button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {cards.map((c) => {
          const clickable = !!c.tab;
          return (
            <div
              key={c.key}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onNavigate(c.tab) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigate(c.tab); } } : undefined}
              className={`bg-white rounded-2xl border border-slate-200 p-5 transition-all ${clickable ? "cursor-pointer hover:border-[#F5551E] hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#F5551E]/40" : ""}`}
              data-testid={`stat-${c.key}`}
            >
              <span className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}18` }}><c.icon size={20} style={{ color: c.color }} /></span>
              <p className="font-display text-3xl font-extrabold text-[#12234A] mt-3">{s[c.key]}</p>
              <p className="text-sm text-slate-500">{c.label}</p>
              {c.key === "conversations" && s.unread_conversations > 0 && (
                <span data-testid="stat-conversations-unread" className="inline-flex items-center gap-1 mt-2 bg-[#F5551E]/10 text-[#F5551E] text-xs font-bold rounded-full px-2 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F5551E]" /> {s.unread_conversations} with unread {s.unread_conversations === 1 ? "reply" : "replies"}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <TrendCharts />
    </>
  );
}

export function FeaturedPerformance() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/featured/performance").then((r) => setD(r.data)).catch(() => setD({ listings: [] })); }, []);
  if (!d) return <Skeleton className="h-40 rounded-2xl mt-4" />;
  if (!d.listings.length) return null;
  const top = d.listings.slice(0, 8);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4" data-testid="featured-performance">
      <div className="flex items-center gap-2 mb-1">
        <Star size={18} className="fill-[#F5551E] text-[#F5551E]" />
        <h3 className="font-display text-lg font-bold text-[#12234A]">Featured performance</h3>
        <span className="ml-auto text-xs text-slate-400">{d.active_count}/{d.max_featured} featured now</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">Views &amp; clicks each listing earned while pinned</p>
      <div className="space-y-1.5">
        {top.map((l, i) => (
          <Link key={l.id} to={`/listing/${l.id}`} data-testid={`featured-perf-${l.id}`}
            className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#FFF7F4] transition-colors">
            <span className="text-sm font-bold text-slate-400 w-5 text-center">{i + 1}</span>
            <img src={photoUrl(l.photo)} alt="" className="h-10 w-12 rounded-lg object-cover bg-slate-100 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[#12234A] truncate flex items-center gap-1.5">
                {l.featured && <Star size={11} className="fill-[#F5551E] text-[#F5551E] shrink-0" />}
                <span className="truncate">{l.title}</span>
              </p>
              <p className="text-xs text-slate-400 truncate">{l.city}, {l.province}</p>
            </div>
            <div className="flex items-center gap-3 text-xs font-medium shrink-0">
              <span className="inline-flex items-center gap-1 text-slate-600"><Eye size={13} /> {l.featured_views}</span>
              <span className="inline-flex items-center gap-1 text-[#F5551E]"><MousePointerClick size={13} /> {l.featured_clicks}</span>
              <span className="hidden sm:inline text-slate-400 w-12 text-right">{l.ctr}% CTR</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
